"use client";

import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useTolgee } from "@tolgee/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import GlobalIcon from "#/assets/icons/nav/global.svg?react";
import { Button } from "#/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover";
import {
	LOCALE_LABELS,
	localeFromPath,
	localizedPathFor,
	SUPPORTED_LOCALES,
	type SupportedLocale,
	stripLocalePrefix,
} from "#/lib/locale";
import { cn } from "#/lib/utils";
import { localeAtom, setLocaleAtom } from "#/stores/locale-store";

export function LanguageSwitcher() {
	const [open, setOpen] = useState(false);
	const currentLocale = useAtomValue(localeAtom);
	const setLocale = useSetAtom(setLocaleAtom);
	const tolgee = useTolgee();
	const navigate = useNavigate();
	const router = useRouter();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const logicalPath = stripLocalePrefix(pathname);

	// Persist the preference + switch translations immediately. On anchor items
	// this runs before the browser follows the href (localStorage is sync).
	function handleSelect(locale: SupportedLocale) {
		setOpen(false);
		setLocale(locale);
		tolgee.changeLanguage(locale);
		// URL 없는 전환(zh, 비로컬라이즈 경로)도 head()가 새 언어로 title/meta를 다시
		// 계산하게 라우터를 무효화한다. /ko 트리 안의 JS 전환은 제외 — beforeLoad가
		// URL 로케일(ko)로 되돌린다 (zh는 아직 URL 트리가 없다).
		if (!localeFromPath(pathname)) router.invalidate();
	}

	function itemClass(locale: SupportedLocale) {
		return cn(
			"flex w-full cursor-pointer items-center rounded-md border border-transparent px-3 py-2 text-body2-medium no-underline transition-colors hover:border-transparent hover:bg-muted dark:hover:bg-[var(--color-theme-fill-strong)]",
			currentLocale === locale ? "text-foreground" : "text-muted-foreground",
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon-sm"
					className="w-10 text-foreground dark:text-foreground hover:text-foreground"
					aria-label="Language"
				>
					<GlobalIcon className="h-4 w-4" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-36 border dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)] p-1"
			>
				{SUPPORTED_LOCALES.map((locale) => {
					// On a page with localized URLs, render a real anchor so crawlers can
					// follow the language link (and cmd/ctrl-click opens a new tab), but
					// intercept plain clicks into an SPA navigation: a full reload
					// destroys the query cache, which made learn pages refetch through a
					// visible loader (계측: /v1/posts 4회 재호출, 콘텐츠 공백 ~850ms).
					// URL/canonical/locale 강제는 pathname 기반(LocaleSync·SeoSync)이라
					// SPA 내비로도 동일하게 동작한다. Locales without a URL for this page
					// (zh, or non-localizable paths like /trade) stay a JS-only switch.
					const target = localizedPathFor(logicalPath, locale);
					if (target && target !== pathname) {
						return (
							<a
								key={locale}
								href={target}
								onClick={(event) => {
									handleSelect(locale);
									// 보조 클릭(새 탭 등)은 브라우저 기본 동작 유지
									if (
										event.metaKey ||
										event.ctrlKey ||
										event.shiftKey ||
										event.altKey ||
										event.button !== 0
									) {
										return;
									}
									event.preventDefault();
									// Cast: target은 런타임에 조립된 구체 경로 — LocalizedLink와
									// 같은 사유의 캐스트 (양쪽 트리 모두 routeTree에 존재).
									navigate({ to: target as never });
								}}
								className={itemClass(locale)}
							>
								{LOCALE_LABELS[locale]}
							</a>
						);
					}
					return (
						<button
							key={locale}
							type="button"
							onClick={() => handleSelect(locale)}
							className={itemClass(locale)}
						>
							{LOCALE_LABELS[locale]}
						</button>
					);
				})}
			</PopoverContent>
		</Popover>
	);
}
